import React, { useContext, useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { Card, Form, Nav, ProgressBar } from 'react-bootstrap';
import classNames from 'classnames';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useForm } from 'react-hook-form';
import Success from './Success';
import AppContext, { AuthWizardContext } from 'context/Context';
import IconButton from 'components/common/IconButton';
import WizardModal from './WizardModal';
import Flex from 'components/common/Flex';
import { faBuilding } from '@fortawesome/free-solid-svg-icons';
import IconAlert from 'components/common/IconAlert';
import { Col, Row } from 'react-bootstrap';
import infiniteLoop from 'assets/img/animated-icons/infinite-loop.json';
import Lottie from 'lottie-react';
import { Alert } from 'react-bootstrap';
import axiosClient from 'config/axios';
import { useNavigate, useParams } from 'react-router-dom';
import BussinessForm from './BussinessForm';
import NewBussinessForm from '../BussinessForm'; 
import ConfigurationForm from './ConfigurationForm';
import NewConfigurationForm from '../ConfigurationForm'; 

const WizardLayout = ({ variant, validation, progressBar }) => {
  const navigate = useNavigate();
  
  const { emailAccountId } = useParams();
  const [showAlert, setShowAlert] = useState();
  const [isLoading, setIsLoading] = useState(false);
  const [triggerErrorCheck, setTriggerErrorCheck] = useState(false);
  const [isError, setIsError] = useState(false);

  const { isRTL } = useContext(AppContext);
  const { emailAccount, setEmail, step, setStep } =
    useContext(AuthWizardContext);
  const {
    register,
    handleSubmit,
    formState: { errors },
    watch,
    setValue,
    getValues,
    reset,
    clearErrors,
    control
  } = useForm();

  const [modal, setModal] = useState(false);
  const [hasFetch, setHasFetch] = useState(false);

  const fetchEmailAccount = async () => {
    try {
      const response = await axiosClient.get(`/email-account/${emailAccountId}`);
      setEmail(response.data.emailAccount);
      setValue("email", response.data.emailAccount.email)

      localStorage.setItem('formData', JSON.stringify(response.data.emailAccount));
      setHasFetch(true)
    } catch (error) {
      setHasFetch(true)
      if(error.response.status === 404){
        navigate("/errors/404")
      }
      console.log(error);
    }
  }

  useEffect(() => {
    localStorage.setItem('currentStep', 1);
    setStep(1);
    fetchEmailAccount()

  }, [emailAccountId]);

  // Empty dependency array means this effect runs once when the component mounts

  const navItems = [
    {
      icon: faBuilding,
      label: 'Bussiness'
    },
    {
      icon: 'cog',
      label: 'Configuration'
    },
    {
      icon: 'thumbs-up',
      label: 'Done'
    }
  ];


  const onSubmitFinalData = async data => {
    try {
      // Submit the final data to the server

      setIsLoading(true);

      console.log(data);

      const response = await axiosClient.post('/handleEmailSubmit', data);

      setIsLoading(false);
      // Check if the data was submitted successfully
      if (response.status === 200) {
        // Handle successful submission (e.g., redirect the user, show a success message, etc.)
         //Need to remove from stroage so user can start from begining 
        localStorage.removeItem('formData');
        localStorage.removeItem('currentStep');
      } else {
        // Handle any errors that occurred while submitting the data
        console.error('Error submitting data:', response);
      }
    } catch (error) {
      console.error('Error submitting data:', error);
    }
  };

  const onSubmitData = async data => {
    if (step === 2) {
      setEmail(pre =>({ ...pre, ...data }));
      await onSubmitFinalData(data);
      setStep(step + 1);
    } else {
      setEmail({ ...emailAccount, ...data });
      setStep(step + 1);
      localStorage.setItem('currentStep', step);
      localStorage.setItem('formData', JSON.stringify(data));
    }
  };

  const onError = () => {
    if (validation) {
      // Display the error messages
      setIsError(true);
      setTriggerErrorCheck(true);
      window.scrollTo(0, 0);
      // Set isError back to false after 3 seconds
      setTimeout(() => {
        setIsError(false);
      }, 6000);
    }
  };

  const toggle = () => setModal(!modal);

  const handleNavs = targetStep => {
    if (step !== 4) {
      if (targetStep < step) {
        setStep(targetStep);
      } else {
        handleSubmit(onSubmitData, onError)();
      }
    } else {
      toggle();
    }
  };

  if (isLoading) {
    return (
      <Row className="justify-content-center mb-3 g-3">
        <Col lg={4} className="d-flex justify-content-center">
          <Lottie
            animationData={infiniteLoop}
            loop={true}
            style={{ height: '120px', width: '120px' }}
          />
        </Col>
        <h3 className="text-center">Loading...</h3>
      </Row>
    );
  }

  // let alertStyle = {
  //   width: "200px",  /* change this value to whatever size you want */
  //   height: "200px" /* change this value to whatever size you want */
  // };

  if (showAlert) {
    return (
      <Row className="justify-content-center align-items-center">
        <IconAlert
          style={{ width: '400px', height: '300px' }}
          variant={'info'}
          dismissible
          onClose={() => setShowAlert(false)}
        >
          <p className="mb-0">
            {
              'Your data has been saved! You will now be redirected to your email provider for authentication.'
            }
          </p>
        </IconAlert>
      </Row>
    );
  } else if(hasFetch) {
    return (
      <>
        {isError && (
          <Alert variant="danger" onClose={() => setIsError(false)} dismissible>
            <Row className="justify-content-center align-items-center">
              <p className="text-center">
                There are validation errors:{' '}
                {Object.values(errors)
                  .map(error => error.message)
                  .join(', ')}
              </p>
            </Row>
          </Alert>
        )}

        <WizardModal modal={modal} setModal={setModal} />
        <Card
          as={Form}
          noValidate
          onSubmit={handleSubmit(onSubmitData, onError)}
          className="theme-wizard mb-5"
        >
          <Card.Header
            className={classNames('bg-light', {
              'px-4 py-3': variant === 'pills',
              'pb-2': !variant
            })}
          >
            <Nav className="justify-content-center" variant={variant}>
              {variant === 'pills'
                ? navItems.map((item, index) => (
                    <NavItemPill
                      key={item.label}
                      index={index + 1}
                      step={step}
                      handleNavs={handleNavs}
                      icon={item.icon}
                      label={item.label}
                    />
                  ))
                : navItems.map((item, index) => (
                    <NavItem
                      key={item.label}
                      index={index + 1}
                      step={step}
                      handleNavs={handleNavs}
                      icon={item.icon}
                      label={item.label}
                    />
                  ))}
            </Nav>
          </Card.Header>
          {progressBar && <ProgressBar now={step * 25} style={{ height: 2 }} />}
            {
                emailAccount.configuration ? 
                <Card.Body className="fw-normal px-md-6 py-4">
                  {step === 1 && (
                    <BussinessForm
                      register={register}
                      errors={errors}
                      setValue={setValue}
                      watch={watch}
                      control={control}
                      getValues={getValues}
                    />
                    
                  )}
                  {step === 2 && (
                    <ConfigurationForm
                      register={register}
                      errors={errors}
                      watch={watch}
                      setValue={setValue}
                      clearErrors={clearErrors}
                      triggerErrorCheck={triggerErrorCheck}
                      setTriggerErrorCheck={setTriggerErrorCheck}
                    />
                  )}
                  {step === 3 && (
                    <Success reset={reset} />
                    )}
                </Card.Body>
      
               : <Card.Body className="fw-normal px-md-6 py-4">
                  {step === 1 && (
                    <NewBussinessForm
                      register={register}
                      errors={errors}
                      setValue={setValue}
                      watch={watch}
                      control={control}
                    />
                    
                  )}
                  {step === 2 && (
                    <NewConfigurationForm
                      register={register}
                      errors={errors}
                      watch={watch}
                      setValue={setValue}
                      clearErrors={clearErrors}
                      triggerErrorCheck={triggerErrorCheck}
                      setTriggerErrorCheck={setTriggerErrorCheck}
                    />
                  )}
                  {step === 3 && (
                    <Success reset={reset} />
                  )}
                </Card.Body>
            }
          <Card.Footer
            className={classNames('px-md-6 bg-light', {
              'd-none': step === 3,
              ' d-flex': step < 3
            })}
          >
            <IconButton
              variant="link"
              icon={isRTL ? 'chevron-right' : 'chevron-left'}
              iconAlign="left"
              transform="down-1 shrink-4"
              className={classNames('px-0 fw-semi-bold', {
                'd-none': step === 1
              })}
              onClick={() => {
                setStep(step - 1);
              }}
            >
              Prev
            </IconButton>

            <IconButton
              variant="primary"
              className="ms-auto px-5"
              type="submit"
              icon={isRTL ? 'chevron-left' : 'chevron-right'}
              iconAlign="right"
              transform="down-1 shrink-4"
            >
              Next
            </IconButton>
          </Card.Footer>
        </Card>
      </>
    );
  }
};

const NavItem = ({ index, step, handleNavs, icon, label }) => {
  return (
    <Nav.Item>
      <Nav.Link
        className={classNames('fw-semi-bold', {
          done: index < 4 ? step > index : step > 3,
          active: step === index
        })}
        onClick={() => handleNavs(index)}
      >
        <span className="nav-item-circle-parent">
          <span className="nav-item-circle">
            <FontAwesomeIcon icon={icon} />
          </span>
        </span>
        <span className="d-none d-md-block mt-1 fs--1">{label}</span>
      </Nav.Link>
    </Nav.Item>
  );
};

const NavItemPill = ({ index, step, handleNavs, icon, label }) => {
  return (
    <Nav.Item>
      <Nav.Link
        className={classNames('fw-semi-bold', {
          done: step > index,
          active: step === index
        })}
        onClick={() => handleNavs(index)}
      >
        <Flex alignItems="center" justifyContent="center">
          <FontAwesomeIcon icon={icon} />
          <span className="d-none d-md-block mt-1 fs--1 ms-2">{label}</span>
        </Flex>
      </Nav.Link>
    </Nav.Item>
  );
};

WizardLayout.propTypes = {
  variant: PropTypes.oneOf(['pills']),
  validation: PropTypes.bool,
  progressBar: PropTypes.bool
};

NavItemPill.propTypes = {
  index: PropTypes.number.isRequired,
  step: PropTypes.number.isRequired,
  handleNavs: PropTypes.func.isRequired,
  icon: PropTypes.string.isRequired,
  label: PropTypes.string.isRequired
};

NavItem.propTypes = NavItemPill.propTypes;

export default WizardLayout;
